import { Component, OnInit, Input, ViewChild, ElementRef } from '@angular/core';
import { RuleMessageService } from '../../../services/rule-message.service';
import { RuleMessage } from '../../../models/rule-message.model';
import { MessageService } from 'primeng/api';
import { GLOBAL_TOAST } from '../../../shared/constants/global-key.constans';
import { AuthService } from '../../../core/services/auth.service';

interface Comment {
  id: string | number;
  userName: string;
  initials: string;
  date: Date;
  text: string;
}

@Component({
  selector: 'app-historical',
  templateUrl: './historical.component.html',
  styleUrl: './historical.component.scss',
  standalone: false,
})
export class HistoricalComponent implements OnInit {
  @Input() ruleUid: string = '';
  @ViewChild('commentTextarea')
  commentTextarea!: ElementRef<HTMLTextAreaElement>;
  comments: Comment[] = [];
  allComments: Comment[] = [];
  newComment: string = '';
  pageSize: number = 5;
  totalComments: number = 0;
  currentPage: number = 0;
  isSending: boolean = false;
  isLoading: boolean = false;
  isEditMode: boolean = false;
  editingCommentId: string | number | null = null;
  currentUserEmail: string = '';

  constructor(
    private ruleMessageService: RuleMessageService,
    private messageService: MessageService,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.getCurrentUserEmail();
    if (this.ruleUid) {
      this.loadMessages();
    }
  }

  getCurrentUserEmail(): void {
    const user = this.authService.getUser();
    this.currentUserEmail = user?.email || '';
  }

  loadMessages(): void {
    this.isLoading = true;

    this.ruleMessageService
      .GetByRuleId({
        ruleId: this.ruleUid,
        first: this.currentPage * this.pageSize,
        rows: this.pageSize,
        sortOrder: -1,
      })
      .subscribe({
        next: (response) => {
          this.comments = response.items.map((msg) => ({
            id: msg.uid,
            userName: msg.sender,
            initials: this.getInitials(msg.sender),
            date: new Date(msg.sentAt),
            text: msg.content,
          }));
          this.totalComments = response.totalRecords;
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error loading messages:', err);
          this.isLoading = false;
        },
      });
  }

  getInitials(name?: string): string {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  loadPage(page: number): void {
    this.currentPage = page;
    this.loadMessages();
  }

  onPageChange(event: any): void {
    this.loadPage(event.page);
  }

  sendComment(): void {
    if (this.newComment.trim() && !this.isSending) {
      this.isSending = true;

      this.ruleMessageService
        .Add({
          content: this.newComment.trim(),
          rule_FK: this.ruleUid,
        })
        .subscribe({
          next: (newMessage: RuleMessage) => {
            this.newComment = '';
            this.isSending = false;
            this.loadMessages();

            if (this.commentTextarea) {
              this.commentTextarea.nativeElement.style.height = 'auto';
            }

            this.messageService.add({
              key: GLOBAL_TOAST,
              severity: 'success',
              summary: 'Succès',
              detail: 'Message ajouté avec succès',
            });
          },
          error: (err) => {
            console.error('Error sending message:', err);
            this.isSending = false;
            this.messageService.add({
              key: GLOBAL_TOAST,
              severity: 'error',
              summary: 'Erreur',
              detail: "Erreur lors de l'envoi du message",
            });
          },
        });
    }
  }

  autoResize(textarea: HTMLTextAreaElement): void {
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }

  onCommentChange(textarea: HTMLTextAreaElement): void {
    if (!this.newComment) {
      textarea.style.height = 'auto';
    }
  }

  canEditComment(comment: Comment): boolean {
    return (
      !!this.currentUserEmail && comment.userName === this.currentUserEmail
    );
  }

  startEditComment(comment: Comment): void {
    this.isEditMode = true;
    this.editingCommentId = comment.id;
    this.newComment = comment.text;

    setTimeout(() => {
      if (this.commentTextarea) {
        const textarea = this.commentTextarea.nativeElement;
        this.autoResize(textarea);
        textarea.focus();
      }
    }, 0);
  }

  updateComment(): void {
    if (this.newComment.trim() && !this.isSending && this.editingCommentId) {
      this.isSending = true;

      this.ruleMessageService
        .Update({
          uid: this.editingCommentId.toString(),
          content: this.newComment.trim(),
        })
        .subscribe({
          next: () => {
            this.cancelEdit();
            this.loadMessages();
            this.isSending = false;

            this.messageService.add({
              key: GLOBAL_TOAST,
              severity: 'success',
              summary: 'Succès',
              detail: 'Commentaire modifié avec succès',
            });
          },
          error: (err) => {
            console.error('Error updating message:', err);
            this.isSending = false;
            this.messageService.add({
              key: GLOBAL_TOAST,
              severity: 'error',
              summary: 'Erreur',
              detail: 'Erreur lors de la modification du commentaire',
            });
          },
        });
    }
  }

  cancelEdit(): void {
    this.isEditMode = false;
    this.editingCommentId = null;
    this.newComment = '';

    if (this.commentTextarea) {
      this.commentTextarea.nativeElement.style.height = 'auto';
    }
  }
}
